/** Texts of the Price Action model (multi-timeframe naked price action): its tuning, its summary, its chart, its checks. Each key in both languages. */
export const naked = {
  en: {
    "nk.name": "Price Action",
    "nk.tune": "Tuning",
    "nk.title": "Price Action tuning",
    "nk.about.title": "Price Action – the strategy",
    "nk.about.intro": "Naked price action over several timeframes: no indicator, only the structure of the swings — trade the break of structure on the entry timeframe in the direction of the higher timeframes, enter at the retest of the broken level.",
    "nk.about.steps": "Structure on 1D and 4H (swings of 2 bars each side): up when the last two confirmed swing highs and lows rise (higher highs and higher lows), down mirrored, else neutral.\nBias: the 4H structure, only where the 1D does not stand against it.\n1H (switchable): its structure (swings of 3 bars) in the bias’s direction; its confirmed swings are the chart’s levels.\nBreak of structure (BOS) on the entry timeframe (15M or 30M, swings of 2 bars): a close beyond its last confirmed swing high (low) in the bias’s direction.\nRetest: a limit at the broken level for 8 bars of the entry timeframe; void at a close beyond the last swing low (high).\nStop: 0.1 ATR beyond that last swing; the risk at most 3 ATR (else no setup). Target: a fixed multiple of the risk (R).\nEntries only 07:00–20:00 London; flat after 48 hours at the latest. One position at a time.\nA swing counts only once the bars after it closed, a higher timeframe’s bar only once closed (the 1D bar at 17:00 New York).",
    "nk.about.evidence": "Measured (round 45, gold 2010 – 10/2026, bid/ask, $0.07 commission, $0.10 slippage, swap 0.01 % a night, PREREG45): negative after costs in every setting. Chosen setting (3 R, entry 30M, 1H off): out of sample 2023–2026 −0.20 R per trade (n 260, profit factor 0.74), before costs −0.15 R; 2010–2026 −0.10 R. No edge — discarded. Shown for analysis only.",
    "nk.r": "{n} R",
    "nk.target": "Target",
    "nk.entryTf": "Entry timeframe",
    "nk.h1": "1H in bias direction",
    "nk.tf.15m": "15M",
    "nk.tf.30m": "30M",
    "nk.preset.1": "Standard",
    "nk.preset.2": "Entry 30M",
    "nk.preset.3": "Without 1H",
    "nk.frames": "1D · 4H · 1H · entry {tf}",
    "nk.long": "Buy",
    "nk.short": "Sell",
    "nk.off": "off",
    "nk.trend.up": "up",
    "nk.trend.down": "down",
    "nk.trend.none": "neutral",
    "nk.row.d1": "Structure 1D",
    "nk.row.h4": "Structure 4H",
    "nk.row.bias": "Bias",
    "nk.row.h1": "Structure 1H",
    "nk.row.window": "Entry window",
    "nk.window.in": "open (07:00–20:00 London)",
    "nk.window.out": "closed (entries 07:00–20:00 London)",
    "nk.status": "Now",
    "nk.status.none": "no setup: no bias or the 1H against it",
    "nk.status.wait": "waiting for the break of structure",
    "nk.step.bias": "Bias 4H",
    "nk.step.biasNote": "1D not against it",
    "nk.step.h1": "1H",
    "nk.step.h1Note": "in bias direction",
    "nk.step.bos": "BOS {tf}",
    "nk.step.bosNote": "close beyond the last swing",
    "nk.step.retest": "Retest",
    "nk.step.retestNote": "limit at the broken level",
    "nk.tip.trade": "{side} running since {time}",
    "nk.tip.order": "{side} limit at the broken level {price}",
    "nk.tip.wait": "waiting for a break of structure on {tf}",
    "nk.tip.none": "no setup on this side",
    "nk.role.active": "active",
    "nk.role.next": "next",
    "nk.kind.retest": "Retest of the BOS",
    "nk.kind.support": "1H support",
    "nk.kind.resistance": "1H resistance",
    "nk.zone.long": "BUY ZONE {price} · {kind}",
    "nk.zone.short": "SELL ZONE {price} · {kind}",
    "nk.fig.limit": "Limit",
    "nk.fig.entry": "Entry",
    "nk.fig.stop": "Stop",
    "nk.fig.target": "Target {r} R",
    "nk.chart.limit": "Limit {arrow} · broken level",
    "nk.chart.entry": "Entry {arrow}",
    "nk.chart.stop": "Stop",
    "nk.chart.target": "Target {r} R",
    "nk.chart.high": "1H high",
    "nk.chart.low": "1H low",
    "chk.nkBias": "Bias",
    "chk.nkBias.hint": "Price Action. ✓ buy while the 4H structure is up (the last two swing highs and lows rise) and the 1D is not down; ✓ sell mirrored.",
    "chk.nkH1": "1H",
    "chk.nkH1.hint": "Price Action. ✓ on the side the 1H structure points to (swings of 3 bars); with the 1H off in the tuning always ✓.",
    "chk.nkWindow": "Entry window",
    "chk.nkWindow.hint": "Price Action. ✓ while the next bar of the entry timeframe starts between 07:00 and 20:00 London.",
    "chk.nkSetup": "Setup",
    "chk.nkSetup.hint": "Price Action. ✓ on its side while a limit waits at the broken level after a break of structure, or its trade runs.",
  },
  de: {
    "nk.name": "Price Action",
    "nk.tune": "Feintuning",
    "nk.title": "Price-Action-Feintuning",
    "nk.about.title": "Price Action – die Strategie",
    "nk.about.intro": "Nackte Price Action über mehrere Zeitebenen: kein Indikator, nur die Struktur der Swings — den Strukturbruch auf der Einstiegszeitebene in Richtung der höheren Zeitebenen handeln, Einstieg am Retest des gebrochenen Levels.",
    "nk.about.steps": "Struktur auf 1D und 4H (Swings mit je 2 Kerzen links und rechts): aufwärts, wenn die letzten zwei bestätigten Swing-Hochs und -Tiefs steigen (höhere Hochs und höhere Tiefs), abwärts gespiegelt, sonst neutral.\nBias: die 4H-Struktur, nur wenn 1D nicht entgegensteht.\n1H (abschaltbar): ihre Struktur (Swings mit 3 Kerzen) in Bias-Richtung; ihre bestätigten Swings sind die Levels im Chart.\nStrukturbruch (BOS) auf der Einstiegszeitebene (15M oder 30M, Swings mit 2 Kerzen): ein Schluss über dem letzten bestätigten Swing-Hoch (unter dem Swing-Tief) in Bias-Richtung.\nRetest: ein Limit am gebrochenen Level, gültig 8 Kerzen der Einstiegszeitebene; ungültig bei einem Schluss jenseits des letzten Swing-Tiefs (-Hochs).\nStop: 0,1 ATR jenseits dieses letzten Swings; Risiko höchstens 3 ATR (sonst kein Setup). Ziel: ein festes Vielfaches des Risikos (R).\nEinstiege nur 07:00–20:00 London; spätestens nach 48 Stunden flach. Immer nur eine Position.\nEin Swing zählt erst, wenn die Kerzen danach geschlossen sind, eine Kerze einer höheren Zeitebene erst geschlossen (die 1D-Kerze um 17:00 New York).",
    "nk.about.evidence": "Gemessen (Runde 45, Gold 2010 – 10/2026, Geld/Brief, 0,07 $ Kommission, 0,10 $ Slippage, Swap 0,01 % je Nacht, PREREG45): in jeder Einstellung nach Kosten negativ. Gewählte Einstellung (3 R, Einstieg 30M, 1H aus): außerhalb 2023–2026 −0,20 R je Trade (n 260, Profit-Faktor 0,74), vor Kosten −0,15 R; 2010–2026 −0,10 R. Kein Vorteil – verworfen. Nur zur Analyse.",
    "nk.r": "{n} R",
    "nk.target": "Ziel",
    "nk.entryTf": "Einstiegszeitebene",
    "nk.h1": "1H in Bias-Richtung",
    "nk.tf.15m": "15M",
    "nk.tf.30m": "30M",
    "nk.preset.1": "Standard",
    "nk.preset.2": "Einstieg 30M",
    "nk.preset.3": "Ohne 1H",
    "nk.frames": "1D · 4H · 1H · Einstieg {tf}",
    "nk.long": "Kauf",
    "nk.short": "Verkauf",
    "nk.off": "aus",
    "nk.trend.up": "aufwärts",
    "nk.trend.down": "abwärts",
    "nk.trend.none": "neutral",
    "nk.row.d1": "Struktur 1D",
    "nk.row.h4": "Struktur 4H",
    "nk.row.bias": "Bias",
    "nk.row.h1": "Struktur 1H",
    "nk.row.window": "Einstiegsfenster",
    "nk.window.in": "offen (07:00–20:00 London)",
    "nk.window.out": "geschlossen (Einstiege 07:00–20:00 London)",
    "nk.status": "Jetzt",
    "nk.status.none": "kein Setup: kein Bias oder 1H dagegen",
    "nk.status.wait": "wartet auf den Strukturbruch",
    "nk.step.bias": "Bias 4H",
    "nk.step.biasNote": "1D nicht dagegen",
    "nk.step.h1": "1H",
    "nk.step.h1Note": "in Bias-Richtung",
    "nk.step.bos": "BOS {tf}",
    "nk.step.bosNote": "Schluss jenseits des letzten Swings",
    "nk.step.retest": "Retest",
    "nk.step.retestNote": "Limit am gebrochenen Level",
    "nk.tip.trade": "{side} läuft seit {time}",
    "nk.tip.order": "{side}-Limit am gebrochenen Level {price}",
    "nk.tip.wait": "wartet auf einen Strukturbruch auf {tf}",
    "nk.tip.none": "kein Setup auf dieser Seite",
    "nk.role.active": "aktiv",
    "nk.role.next": "nächste",
    "nk.kind.retest": "Retest des BOS",
    "nk.kind.support": "1H-Unterstützung",
    "nk.kind.resistance": "1H-Widerstand",
    "nk.zone.long": "BUY ZONE {price} · {kind}",
    "nk.zone.short": "SELL ZONE {price} · {kind}",
    "nk.fig.limit": "Limit",
    "nk.fig.entry": "Einstieg",
    "nk.fig.stop": "Stop",
    "nk.fig.target": "Ziel {r} R",
    "nk.chart.limit": "Limit {arrow} · gebrochenes Level",
    "nk.chart.entry": "Einstieg {arrow}",
    "nk.chart.stop": "Stop",
    "nk.chart.target": "Ziel {r} R",
    "nk.chart.high": "1H-Hoch",
    "nk.chart.low": "1H-Tief",
    "chk.nkBias": "Bias",
    "chk.nkBias.hint": "Price Action. ✓ Kauf, solange die 4H-Struktur aufwärts ist (die letzten zwei Swing-Hochs und -Tiefs steigen) und 1D nicht abwärts; ✓ Verkauf gespiegelt.",
    "chk.nkH1": "1H",
    "chk.nkH1.hint": "Price Action. ✓ auf der Seite, in die die 1H-Struktur zeigt (Swings mit 3 Kerzen); mit 1H aus im Feintuning immer ✓.",
    "chk.nkWindow": "Einstiegsfenster",
    "chk.nkWindow.hint": "Price Action. ✓, solange die nächste Kerze der Einstiegszeitebene zwischen 07:00 und 20:00 London beginnt.",
    "chk.nkSetup": "Setup",
    "chk.nkSetup.hint": "Price Action. ✓ auf seiner Seite, solange nach einem Strukturbruch ein Limit am gebrochenen Level wartet oder sein Trade läuft.",
  },
};
